import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '@/api/client'

interface AuthContextType {
  user: { email: string; name?: string } | null
  accessToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => string | null
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
  getAccessToken: () => null,
})

function parseJwt(token: string) {
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [user, setUser] = useState<{ email: string; name?: string } | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // On mount, try to refresh token from cookie
  useEffect(() => {
    api.post<{ access_token: string; expires_in: number }>('/auth/refresh')
      .then((data) => {
        setAccessToken(data.access_token)
        const payload = parseJwt(data.access_token)
        if (payload) {
          setUser({ email: payload.email, name: payload.name })
        }
      })
      .catch(() => {
        // No valid refresh token — user is not logged in
      })
      .finally(() => setIsLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string, rememberMe = false) => {
    const data = await api.post<{ access_token: string; expires_in: number }>('/auth/login', {
      username: email,
      password,
      rememberMe,
    })
    setAccessToken(data.access_token)
    const payload = parseJwt(data.access_token)
    if (payload) {
      setUser({ email: payload.email, name: payload.name })
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } catch {
      // Best-effort
    }
    setAccessToken(null)
    setUser(null)
  }, [])

  const getAccessToken = useCallback(() => accessToken, [accessToken])

  const value = useMemo(
    () => ({
      user,
      accessToken,
      isAuthenticated: !!accessToken,
      isLoading,
      login,
      logout,
      getAccessToken,
    }),
    [user, accessToken, isLoading, login, logout, getAccessToken],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
